"use client";

import React, { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import Terminal from "@/components/ui/Terminal";
import TerminalOutput from "@/components/ui/TerminalOutput";
import TerminalInput from "@/components/ui/TerminalInput";
import TypingEffect from "@/components/ui/TypingEffect";
import Link from "next/link";

const skills = {
  programming: ["C", "C++", "JavaScript", "TypeScript", "Python", "Go", "Rust"],
  frontend: ["React", "Next", "Remix", "Svelte", "Astro", "Vue", "TailwindCSS", "SCSS"],
  backend: ["Express", "Nest", "Fastify", "Hono", "Go STD", "Fiber", "Echo", "axum"],
  data: ["Spark", "Airflow", "Hadoop", "PowerBI"],
  "Cross-platform": ["Electron", "Tuari", "React Native", "Lynx"],
  others: ["Git", "CircleCI", "Jenkins", "Docker", "Kubernetes", "Kafka", "LangChain", "LlamaIndex"],
};

export default function HeroInteractive() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [terminalOutputs, setTerminalOutputs] = useState<React.ReactNode[]>([]);
  const terminalInputRef = React.useRef<HTMLDivElement>(null);
  const router = useRouter();

  const handleCommand = (command: string) => {
    let output: React.ReactNode;

    switch (command.trim().toLowerCase()) {
      case "help":
        output = (
          <div className="space-y-1">
            <p className="text-terminal-yellow">Available commands:</p>
            <p><span className="text-terminal-green">about</span> - Learn more about me</p>
            <p><span className="text-terminal-green">skills</span> - View my skills</p>
            <p><span className="text-terminal-green">contact</span> - Get in touch</p>
            <p><span className="text-terminal-green">blog</span> - Read my blog</p>
            <p><span className="text-terminal-green">projects</span> - View my projects</p>
            <p><span className="text-terminal-green">tools</span> - View my tools</p>
            <p><span className="text-terminal-green">clear</span> - Clear the terminal</p>
          </div>
        );
        break;

      case "about":
        router.push("/#about");
        output = <p>Navigating to the About section...</p>;
        break;

      case "skills":
        output = (
          <div className="space-y-4">
            <h3 className="text-xl font-semibold text-center lg:text-left">My Skills</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {Object.entries(skills).map(([category, skillList]) => (
                <div key={category} className="terminal-glass p-3 rounded-md">
                  <h4 className={`text-sm font-medium mb-2 ${
                    category === 'frontend' ? 'text-terminal-pink' :
                    category === 'backend' ? 'text-terminal-green' :
                    category === 'programming' ? 'text-terminal-yellow' :
                    category === 'data' ? 'text-terminal-purple' :
                    category === 'others' ? 'text-terminal-blue' :
                    'text-terminal-purple'
                  }`}>
                    {category === 'programming' ? 'Programming' :
                     category.charAt(0).toUpperCase() + category.slice(1)}
                  </h4>
                  <ul className="space-y-1 text-sm">
                    {skillList.map(skill => (
                      <li key={skill} className="flex items-center gap-1">
                        <span className="text-terminal-blue">•</span>
                        <Link
                          href={`https://www.google.com/search?q=${skill}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-terminal-blue hover:underline"
                          dangerouslySetInnerHTML={{ 
                            __html: skill.replace(': ', ': <span class="font-semibold">') + '</span>' 
                          }} 
                        />
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        );
        break;

      case "contact":
        router.push("/#contact");
        output = <p>Navigating to the Contact section...</p>;
        break;

      case "tools":
        output = <p>Navigating to tools page...</p>;
        router.push("/tools");
        break;

      case "projects":
        output = <p>Navigating to projects page...</p>;
        router.push("/projects");
        break;

      case "blog":
        output = <p>Navigating to blog page...</p>;
        router.push("/blog");
        break;

      case "clear":
        setTerminalOutputs([]);
        return;

      default:
        output = (
          <p className="text-terminal-pink">
            Command not found: {command}. Type 'help' for available commands.
          </p>
        );
    }

    setTerminalOutputs([
      ...terminalOutputs,
      <TerminalOutput key={terminalOutputs.length} prompt="$" command={command}>
        {output}
      </TerminalOutput>
    ]);
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setTerminalOutputs([
        <TerminalOutput key="welcome" colorful>
          <p className="text-terminal-green">Welcome to iamickdev&apos;s site!</p>
          <pre className="text-terminal-pink">
            &nbsp;&nbsp;&nbsp;/\  /\<br />
            =( ´ •⁠ω• ⁠)=   𝗓 𐰁 .ᐟ ⣼⣿⡗ ʰⁱ ᵐᵉᵒʷ~<br />
            / ͡      ︵\&nbsp;&nbsp;ᶻ <br />
            (⁠人_____づ_づ<br />
          </pre>
          <p className="mt-2 text-muted-foreground">
            Type <span className="text-terminal-yellow">'help'</span> to see available commands.
          </p>
        </TerminalOutput>
      ]);
      setShowPrompt(true);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="w-full  mx-auto lg:mx-0 mt-8 lg:mt-0 overflow-hidden">
      <Terminal 
        title="~/iamickdev" 
        className="h-[300px] sm:h-[350px]" 
        ref={terminalInputRef as React.RefObject<HTMLDivElement>}
      >
        {terminalOutputs}
        {showPrompt && (
          <TerminalInput
            prompt="$"
            onCommand={handleCommand}
            containerRef={terminalInputRef as React.RefObject<HTMLDivElement>}
          />
        )}
      </Terminal>
    </div>
  );
}